import React, {useRef, useState, useCallback} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Portal,
    Text,
    useMultiStyleConfig,
    useStyleConfig,
    PopoverBody,
    Flex,
    useMediaQuery,
} from '@chakra-ui/react'
import {remCalc} from '../../theme/_utils'
import {AccountIcon} from '../icons'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useIntl} from 'react-intl'

import {AuthHelpers, useAuthHelper, useCustomerType} from '@salesforce/commerce-sdk-react'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {useGigya} from '../../context/gigya'
import {accountMessages} from '../../pages/account/constant'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import {useGetMainMenu} from '../../custom-api/hooks/use-get-main-menu'
import {getMrtApiBasepath} from '../../ssr/helpers'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {SA_IMPERSONATING_KEY, SA_TOKEN_KEY} from '../../hooks/use-sales-assistant-session'
import {useEndImpersonation} from '../../hooks/use-end-impersonation'

const popoverLinks = [
    {name: 'orderHistory', path: '/orders'},
    {name: 'address', path: '/addresses'},
    {name: 'registeredProducts', path: '/registered-products'},
]

function getGuestPopoverLinkProps(menuItemsStyle) {
    return {
        ...menuItemsStyle.menuItem,
        ...menuItemsStyle.text,
        textStyle: 'body-small',
        fontWeight: 'normal',
        display: 'block',
        textAlign: 'left',
        sx: {px: remCalc(8), textDecoration: 'none'},
        _hover: {
            bg: 'background.base.under',
            borderRadius: 'large',
            textDecoration: 'none',
        },
        _focusVisible: {boxShadow: 'outline'},
    }
}

export default function HeaderAccount({setShowLoading, headerLinks}) {
    const {isRegistered} = useCustomerType()

    return isRegistered && isHydrated() ? (
        <LoggedAccountElement setShowLoading={setShowLoading} />
    ) : (
        <GuestAccountElement headerLinks={headerLinks} />
    )
}

function LoggedAccountElement({setShowLoading}) {
    const intl = useIntl()
    const styles = useMultiStyleConfig('Header', {})
    const filtersStyles = useMultiStyleConfig('Filters')
    const menuItemsStyle = useStyleConfig('SortByAccordionItem', {variant: 'sortByButton'})

    const [isExpandedHeader] = useMediaQuery('(min-width: 1200px)', {
        ssr: true,
        fallback: false,
    })

    const {gigyaObject} = useGigya()
    const logout = useAuthHelper(AuthHelpers.Logout)
    const navigate = useNavigation()
    const endImpersonation = useEndImpersonation()

    const [isPopoverOpen, setPopoverOpen] = useState(false)
    const triggerRef = useRef()
    const closeTimer = useRef()

    const handleMouseEnter = useCallback(() => {
        clearTimeout(closeTimer.current)
        setPopoverOpen(true)
    }, [])
    const handleMouseLeave = useCallback(() => {
        closeTimer.current = setTimeout(() => setPopoverOpen(false), 100)
    }, [])

    const onSignoutClick = useCallback(async () => {
        setShowLoading(true)

        // Detect a sales-assistant "login on behalf" session: in that case the
        // logout must end only the impersonation and keep the agent signed in.
        let impersonating = false
        let salesAssistantToken = null
        try {
            impersonating = !!localStorage.getItem(SA_IMPERSONATING_KEY)
            salesAssistantToken = localStorage.getItem(SA_TOKEN_KEY)
        } catch {
            // localStorage may be unavailable in some environments
        }

        if (impersonating && salesAssistantToken) {
            await endImpersonation({onSettled: () => setShowLoading(false)})
            return
        }

        gigyaObject && gigyaObject.accounts && gigyaObject.accounts.logout()
        await logout.mutateAsync()

        setShowLoading(false)
        const endpoint = origin + getMrtApiBasepath('/logout')
        await fetch(`${endpoint}`).then(() => {
            setTimeout(() => {
                navigate('/login')
            }, 500)
        })
    }, [gigyaObject, logout, navigate, endImpersonation, setShowLoading])

    const {data: customer} = useCurrentCustomer()

    const initial = customer?.firstName?.charAt(0) || ''
    const initialsTextStyles = {
        color: 'foreground.neutral.inverse',
        textStyle: 'body-xxsmall',
        fontWeight: 'bold',
        lineHeight: 1,
    }

    if (!isExpandedHeader) {
        return (
            <Box
                as={Link}
                to={`/account`}
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={remCalc(32)}
                height={remCalc(32)}
            >
                <Button
                    ref={triggerRef}
                    aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.my_account',
                        defaultMessage: 'My Account',
                    })}
                    sx={{
                        ...styles.headerButton,
                        backgroundColor: 'transparent',
                        borderRadius: 0,
                        padding: remCalc(6),
                    }}
                    _hover={{backgroundColor: 'transparent'}}
                    {...styles.buttonIcons}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        width={remCalc(20)}
                        height={remCalc(20)}
                        backgroundColor="background.accent.muted"
                        borderRadius="full"
                    >
                        <Text sx={initialsTextStyles}>{initial.toUpperCase()}</Text>
                    </Box>
                </Button>
            </Box>
        )
    }

    return (
        <Popover
            isLazy
            placement="bottom-end"
            offset={[58, 14]}
            isOpen={isPopoverOpen}
            onClose={() => setPopoverOpen(false)}
        >
            <PopoverTrigger>
                <Button
                    ref={triggerRef}
                    aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.my_account',
                        defaultMessage: 'My Account',
                    })}
                    sx={{
                        ...styles.headerButton,
                        backgroundColor: 'transparent',
                        borderRadius: 0,
                        padding: remCalc(6),
                    }}
                    _hover={{backgroundColor: 'transparent'}}
                    {...styles?.buttonIcons}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => setPopoverOpen((open) => !open)}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        width={remCalc(20)}
                        height={remCalc(20)}
                        backgroundColor="background.accent.muted"
                        borderRadius="full"
                    >
                        <Text sx={initialsTextStyles}>{initial.toUpperCase()}</Text>
                    </Box>
                </Button>
            </PopoverTrigger>
            {/* prettier-ignore */}
            <Portal>
                <PopoverContent
                    w={remCalc(228)}
                    maxW={remCalc(228)}
                    maxH={remCalc(320)}
                    {...filtersStyles.sortByFilterMenuList}
                    zIndex={9999}
                    border="1px solid"
                    borderColor="var(--neutral-xs, rgba(19, 24, 58, 0.08))"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <PopoverBody p={0}>
                        <Flex {...filtersStyles.sortByMenuWrapper} p={0}>
                            <Flex
                                {...menuItemsStyle?.menuContainer}
                                p={remCalc(4)}
                                gap={remCalc(2)}
                            >
                                <Box
                                    as={Link}
                                    to={`/account`}
                                    {...menuItemsStyle.menuItem}
                                    sx={{ px: remCalc(8), textDecoration: 'none' }}
                                    display="block"
                                    textAlign="left"
                                    _hover={{
                                        bg: 'background.base.under',
                                        borderRadius: 'large',
                                        textDecoration: 'none',
                                    }}
                                    _focusVisible={{ boxShadow: 'outline' }}
                                >
                                    <Text {...menuItemsStyle?.text} fontWeight="regular">
                                        {intl.formatMessage({
                                            defaultMessage: 'My Account',
                                            id: 'header.popover.title.my_account',
                                        })}
                                    </Text>
                                </Box>
                                {popoverLinks.map((link) => (
                                    <Box
                                        as={Link}
                                        to={`/account${link.path}`}
                                        key={link.name}
                                        {...menuItemsStyle.menuItem}
                                        sx={{ px: remCalc(8), textDecoration: 'none' }}
                                        display="block"
                                        textAlign="left"
                                        _hover={{
                                            bg: 'background.base.under',
                                            borderRadius: 'large',
                                            textDecoration: 'none',
                                        }}
                                        _focusVisible={{ boxShadow: 'outline' }}
                                        aria-label={intl.formatMessage(accountMessages[link.name])}
                                    >
                                        <Text {...menuItemsStyle?.text} fontWeight="regular">
                                            {intl.formatMessage(accountMessages[link.name])}
                                        </Text>
                                    </Box>
                                ))}
                                <Box
                                    as="button"
                                    {...menuItemsStyle.menuItem}
                                    sx={{ px: remCalc(8) }}
                                    display="block"
                                    textAlign="left"
                                    width="100%"
                                    _hover={{ bg: 'background.base.under', borderRadius: 'large' }}
                                    _focusVisible={{ boxShadow: 'outline' }}
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Log out',
                                        id: 'header.popover.action.log_out',
                                    })}
                                    onClick={() => onSignoutClick()}
                                >
                                    <Text {...menuItemsStyle?.text} fontWeight="regular">
                                        {intl.formatMessage({
                                            defaultMessage: 'Log out',
                                            id: 'header.popover.action.log_out',
                                        })}
                                    </Text>
                                </Box>
                            </Flex>
                        </Flex>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    )
}

function GuestAccountElement({headerLinks = []}) {
    const styles = useMultiStyleConfig('Header', {})
    const filtersStyles = useMultiStyleConfig('Filters')
    const menuItemsStyle = useStyleConfig('SortByAccordionItem', {variant: 'sortByButton'})
    const guestPopoverLinkProps = getGuestPopoverLinkProps(menuItemsStyle)
    const intl = useIntl()
    // Removed unused showPopover state

    const [isPopoverOpen, setPopoverOpen] = useState(false)
    const triggerRef = useRef()
    const closeTimer = useRef()

    const handleMouseEnter = useCallback(() => {
        clearTimeout(closeTimer.current)
        setPopoverOpen(true)
    }, [])
    const handleMouseLeave = useCallback(() => {
        closeTimer.current = setTimeout(() => setPopoverOpen(false), 100)
    }, [])

    const [isExpandedHeader] = useMediaQuery('(min-width: 1200px)', {
        ssr: true,
        fallback: false,
    })

    if (!isExpandedHeader) {
        return (
            <Box as={Link} to={`/login`}>
                <Button
                    ref={triggerRef}
                    icon={<AccountIcon sx={styles.headerIcon} />}
                    data-testid="account-icon"
                    aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.my_account',
                        defaultMessage: 'My Account',
                    })}
                    size="base"
                    variant="ghost"
                    {...styles.buttonIcons}
                >
                    <AccountIcon {...styles.accountIcon} />
                </Button>
            </Box>
        )
    }

    return (
        <Popover
            isLazy
            placement="bottom-end"
            isOpen={isPopoverOpen}
            onClose={() => setPopoverOpen(false)}
        >
            <PopoverTrigger>
                <Button
                    ref={triggerRef}
                    icon={<AccountIcon sx={styles.headerIcon} />}
                    data-testid="account-icon"
                    aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.my_account',
                        defaultMessage: 'My Account',
                    })}
                    variant="ghost"
                    {...styles.buttonIcons}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => setPopoverOpen((open) => !open)}
                >
                    <AccountIcon {...styles.accountIcon} />
                </Button>
            </PopoverTrigger>
            {/* prettier-ignore */}
            <Portal>
                <PopoverContent
                    w={remCalc(228)}
                    maxW={remCalc(228)}
                    maxH={remCalc(320)}
                    {...filtersStyles.sortByFilterMenuList}
                    zIndex={9999}
                    border="1px solid"
                    borderColor="var(--neutral-xs, rgba(19, 24, 58, 0.08))"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <PopoverBody p={0}>
                        <Flex {...filtersStyles.sortByMenuWrapper} p={0}>
                            <Flex
                                {...menuItemsStyle?.menuContainer}
                                p={remCalc(4)}
                                gap={remCalc(2)}
                            >
                                <Box
                                    {...guestPopoverLinkProps}
                                    as={Link}
                                    to="/login"
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Login',
                                        id: 'header.popover.action.login',
                                    })}
                                >
                                    {intl.formatMessage({
                                        defaultMessage: 'Login',
                                        id: 'header.popover.action.login',
                                    })}
                                </Box>
                                <Box
                                    {...guestPopoverLinkProps}
                                    as={Link}
                                    to="/registration"
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Register now',
                                        id: 'header.popover.action.register_now',
                                    })}
                                >
                                    <Text as="span">
                                        {intl.formatMessage({
                                            defaultMessage: 'New User?',
                                            id: 'header.popover.text.new_user',
                                        })}
                                    </Text>
                                    {' '}
                                    {intl.formatMessage({
                                        defaultMessage: 'Register now',
                                        id: 'header.popover.action.register_now',
                                    })}
                                </Box>
                                <Box
                                    {...guestPopoverLinkProps}
                                    as={Link}
                                    to="/product-registration"
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Product Registration',
                                        id: 'drawerMenu.productRegistration',
                                    })}
                                >
                                    {intl.formatMessage({
                                        defaultMessage: 'Product Registration',
                                        id: 'drawerMenu.productRegistration',
                                    })}
                                </Box>
                                <HeaderLinksForLgNotXL headerLinks={headerLinks} />
                            </Flex>
                        </Flex>
                    </PopoverBody>
                    {/**Leaving this commmented out for referenc when we imple,ent the other brands redesign */}
                    {/* <PopoverBody
                        borderBottom="small"
                        borderColor="border.light"
                        px={remCalc(16)}
                        py={remCalc(24)}
                    >
                        <SimpleGrid gap={remCalc(24)}>
                            <Button
                                as={Link}
                                to="/login"
                                variant="primary"
                                aria-label={intl.formatMessage({
                                    defaultMessage: 'Login',
                                    id: 'header.popover.action.login',
                                })}
                            >
                                {intl.formatMessage({
                                    defaultMessage: 'Login',
                                    id: 'header.popover.action.login',
                                })}
                            </Button>
                            <Flex gap={remCalc(16)} alignItems="center">
                                <Text>
                                    {intl.formatMessage({
                                        defaultMessage: 'New User?',
                                        id: 'header.popover.text.new_user',
                                    })}
                                </Text>
                                <Button
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Register now',
                                        id: 'header.popover.action.register_now',
                                    })}
                                    as={Link}
                                    to="/registration"
                                    variant="text-button"
                                >
                                    {intl.formatMessage({
                                        defaultMessage: 'Register now',
                                        id: 'header.popover.action.register_now',
                                    })}
                                </Button>
                            </Flex>
                            {BRAND_KEY === 'kenwood' && (
                                <GuestProductRegistrationLink brand={BRAND_KEY} />
                            )}
                            {BRAND_KEY === 'braun' && (
                                <GuestProductRegistrationLink brand={BRAND_KEY} />
                            )}
                        </SimpleGrid>
                        <Box
                            sx={styles.showOnLgOnly}
                            borderTop="small"
                            borderColor="border.light"
                            mt={remCalc(11)}
                            mx={remCalc(-16)}
                        />
                        <SimpleGrid sx={styles.showOnLgOnly} gap={remCalc(16)} pt={remCalc(16)}>
                            <HeaderLinksForLgNotXL headerLinks={headerLinks} />
                        </SimpleGrid>
                    </PopoverBody> */}
                </PopoverContent>
            </Portal>
        </Popover>
    )
}

function GuestProductRegistrationLink({brand}) {
    const intl = useIntl()
    const {data} = useGetMainMenu(`${brand}_`)
    const headerLinks = data?.headerlinks?.[0]?.links ?? []
    if (headerLinks?.[1]) {
        return (
            <Flex gap={remCalc(16)} key={headerLinks?.[1]?._metadata?.uid}>
                <Text
                    aria-label={intl.formatMessage({
                        defaultMessage: 'Product Registration',
                        id: 'drawerMenu.productRegistration',
                    })}
                    as={Link}
                    to={headerLinks?.[1].link?.href || '/registration'}
                >
                    {headerLinks?.[1].link?.title}
                </Text>
            </Flex>
        )
    }
}

function HeaderLinksForLgNotXL({headerLinks = []}) {
    const styles = useMultiStyleConfig('Header')
    const menuItemsStyle = useStyleConfig('SortByAccordionItem', {variant: 'sortByButton'})
    const guestPopoverLinkProps = getGuestPopoverLinkProps(menuItemsStyle)

    return headerLinks?.map((headerLink) => {
        const ctaUrl = headerLink?.link?.href

        const buttonProps = headerLink?.is_external_url
            ? {
                  as: 'a',
                  href: ctaUrl,
                  target: '_blank',
                  rel: 'noopener noreferrer',
              }
            : {
                  as: Link,
                  to: ctaUrl,
              }

        return (
            <Box
                key={headerLink._metadata.uid}
                {...guestPopoverLinkProps}
                sx={{...guestPopoverLinkProps.sx, ...styles.showOnLgOnly}}
                aria-label={headerLink?.link?.title}
                {...buttonProps}
            >
                {headerLink?.link?.title}
            </Box>
        )
    })
}

GuestProductRegistrationLink.propTypes = {
    brand: PropTypes.string,
}

HeaderAccount.propTypes = {
    setShowLoading: PropTypes.func,
    headerLinks: PropTypes.array,
}

GuestAccountElement.propTypes = {
    headerLinks: PropTypes.array,
}

LoggedAccountElement.propTypes = {
    setShowLoading: PropTypes.func,
}
